import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ClipboardPen,
  MapPinned,
  CameraIcon,
  FileBadge,
  ShieldCheck,
  Search,
  Users,
  ScrollText,
  ArrowRight,
  Home as HomeIcon,
  Radar,
} from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { CountUp } from "@/components/marketing/CountUp";
import { HeroSiteMap, CaseTicker } from "@/components/marketing/HeroSiteMap";
import { Navbar } from "@/components/marketing/Navbar";
import { Footer } from "@/components/marketing/Footer";
import { SectionLabel } from "@/components/marketing/SectionLabel";
import { PillButton } from "@/components/marketing/PillButton";
import { USMap } from "@/components/marketing/USMap";
import { VerifiedStamp } from "@/components/marketing/VerifiedStamp";
import { CertificationGrid } from "@/components/marketing/CertificationGrid";
import { MARKETING_THEME } from "@/lib/theme";

const AVATARS = ["a1", "a2", "a3", "a4"];

const AUDIENCES = [
  { id: "business", img: "/img/people/team.jpg", eyebrow: "For businesses", title: "Make decisions on facts, not forms.", text: "Order a consent-based check in minutes and get a clear, reviewed answer about where your customer lives.", to: "/how-it-works/business", cta: "How it works for businesses", tint: "blob-mint bg-oath-accent-light" },
  { id: "individuals", img: "/img/people/phone.jpg", eyebrow: "For individuals", title: "Got a request? You stay in control.", text: "Say yes or no, add your own documents for free, and see your result — all from your phone.", to: "/how-it-works/individuals", cta: "How it works for individuals", tint: "blob-peach bg-oath-accent-light" },
];

const AvatarStack = () => (
  <div className="mt-8 flex items-center gap-4" data-testid="hero-avatar-stack">
    <div className="flex shrink-0 -space-x-3">
      {AVATARS.map((a) => <img key={a} src={`/img/people/${a}.jpg`} alt="" className="h-11 w-11 rounded-full border-2 border-white object-cover shadow-sm" />)}
    </div>
    <p className="max-w-[15rem] text-sm leading-snug text-oath-secondary">Every check starts with a real person saying <b className="text-oath-ink">yes</b>.</p>
  </div>
);

const Audiences = () => (
  <section className="py-16" data-testid="audience-section">
    <div className="mx-auto grid max-w-7xl gap-6 px-6 md:grid-cols-2 lg:px-10">
      {AUDIENCES.map((a) => (
        <Link key={a.id} to={a.to} data-testid={`audience-card-${a.id}`}
          className={`group overflow-hidden rounded-3xl border border-gray-200 ${a.tint} transition-transform duration-300 hover:-translate-y-1`}>
          <div className="aspect-[16/9] overflow-hidden"><img src={a.img} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
          <div className="p-7">
            <div className="text-sm font-bold text-oath-accent">{a.eyebrow}</div>
            <h3 className="mt-2 font-display text-2xl font-bold text-oath-ink">{a.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-oath-secondary sm:text-base">{a.text}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-oath-ink">{a.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const Divider = () => <div className="mx-auto max-w-7xl border-t border-gray-200/80" />;

const INSTRUMENTS = [
  {
    icon: MapPinned,
    no: "NO. 01",
    title: "Current Address Verification",
    desc: "Confirms that the subject presently resides at the declared address. We triangulate postal registry data, utility connection footprints, and — where required — a documented field visit with timestamped photographic evidence.",
    tags: ["Change-of-address registry", "Utility connection footprint", "Geotagged field evidence"],
    metrics: [["TAT", "48H"], ["CONFIDENCE", "99.1%"]],
  },
  {
    icon: Users,
    no: "NO. 02",
    title: "Household Composition",
    desc: "Establishes who actually lives at the address: adults, dependents, and declared versus detected occupants. Built for benefit eligibility, dependent claims, and multi-occupancy underwriting.",
    tags: ["Occupant count reconciliation", "Dependent documentation", "Undeclared-occupant signals"],
    metrics: [["TAT", "72H"], ["SOURCES", "2 MIN."]],
  },
  {
    icon: HomeIcon,
    no: "NO. 03",
    title: "Occupancy Status Confirmation",
    desc: "Owner-occupied, rented, vacant, or misrepresented. Occupancy misstatement is the single largest driver of early-payment default in residential lending — we confirm it before you close.",
    tags: ["Owner / renter classification", "Vacancy & abandonment signals", "Short-term-rental sweep"],
    metrics: [["TAT", "48H"], ["EVIDENCE", "PHOTO"]],
  },
  {
    icon: Radar,
    no: "NO. 04",
    title: "Continued Residence Monitoring",
    desc: "A one-time check decays. Our monitoring program re-verifies on a quarterly cadence and flags address drift, new occupants, or vacancy signals the moment they appear in the data.",
    tags: ["Quarterly re-verification", "Address-drift alerting", "Portfolio exposure dashboard"],
    metrics: [["CYCLE", "QUARTERLY"], ["DELIVERY", "API"]],
  },
];

const SERVICES = [
  {
    icon: ShieldCheck,
    no: "NO. 01",
    title: "Underwriting & Rating",
    desc: "Confirm the garaging address and true resident count before a policy binds, so risk is priced on where people actually live.",
    tags: ["Registry match", "Occupancy signal"],
  },
  {
    icon: Search,
    no: "NO. 02",
    title: "Claims Investigation",
    desc: "Reconstruct documented residency and household facts for suspicious losses, post-loss occupancy, and residency disputes.",
    tags: ["Timeline evidence", "Field confirm"],
  },
  {
    icon: Users,
    no: "NO. 03",
    title: "Program Eligibility",
    desc: "Reconcile the declared household roster against evidence for benefit, subsidy, and dependent-coverage determinations.",
    tags: ["Roster audit", "Dependent check"],
  },
];

const STEPS = [
  {
    tag: "STEP / 01",
    img: "/img/people/couple-phone.jpg",
    icon: ClipboardPen,
    title: "Intake & consent",
    desc: "A case is submitted by console, bulk upload, or API. The subject signs electronic consent, then the declared address and roster lock as the assertion under test.",
  },
  {
    tag: "STEP / 02",
    img: "/img/people/support.jpg",
    icon: MapPinned,
    title: "Data triangulation",
    desc: "Postal registries, utility footprints, and property rolls are cross-checked. Every signal is logged with source, result, and retrieval time.",
  },
  {
    tag: "STEP / 03",
    img: "/img/people/agent.jpg",
    icon: CameraIcon,
    title: "Field confirmation",
    desc: "When signals conflict or risk is high, a vetted agent runs a geotagged visit — reviewed and approved by a separate second operator.",
  },
  {
    tag: "STEP / 04",
    img: "/img/people/moving.jpg",
    icon: FileBadge,
    title: "Assurance report",
    desc: "A signed report issues one verdict — Verified, Discrepant, or Unverifiable — with the full evidence chain, reproducible under audit.",
  },
];

const ProblemStats = () => {
  const { problem_stats: stats } = useSiteContent();
  return (
    <div className="mt-12 grid gap-4 md:grid-cols-3" data-testid="problem-stats">
      {stats.map((s, i) => (
        <article key={i} className="rounded-2xl border border-gray-200 bg-white p-6" data-testid={`problem-stat-${i}`}>
          <CountUp stat={s} className="font-display text-5xl font-bold tracking-tight text-oath-ink" />
          <h3 className="mt-4 font-display text-lg font-bold text-oath-ink">{s.caption}</h3>
          <p className="mt-2 text-sm leading-relaxed text-oath-secondary">{s.note}</p>
        </article>
      ))}
    </div>
  );
};

const CoverageStats = () => {
  const { stats } = useSiteContent();
  return (
    <div className="grid grid-cols-2 gap-3">
      {stats.map((s, i) => (
        <div key={i} data-testid={`coverage-stat-${i}`} className="flex flex-col justify-between rounded-2xl border border-gray-200 bg-white p-6">
          <CountUp stat={s} className="font-display text-4xl font-bold tracking-tight text-oath-ink" />
          <span className="mt-4 font-mono-label text-[9px] text-oath-muted">{s.caption.toUpperCase()}</span>
        </div>
      ))}
    </div>
  );
};

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
      }, 60);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className={`min-h-screen bg-oath-bg ${MARKETING_THEME}`}>
      <Navbar />

      {/* ---------------- 01 HERO ---------------- */}
      <section className="relative overflow-hidden oath-grid pt-36 pb-12">
        <div className="mx-auto grid max-w-7xl items-end gap-10 px-6 lg:grid-cols-[1.15fr_1fr] lg:px-10">
          <div className="animate-fade-up">
            <SectionLabel number="00">PROOF OF RESIDENCE</SectionLabel>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-oath-ink sm:text-5xl lg:text-6xl">
              Documented proof of where people{" "}
              <span className="text-oath-accent">actually live</span> — and who shares the home.
            </h1>
          </div>
          <div className="animate-fade-up lg:pb-2" style={{ animationDelay: "120ms" }}>
            <p className="max-w-xl text-lg leading-relaxed text-oath-secondary">
              ResidenceProof independently verifies declared residential addresses and
              household composition — cross-checked against registries, utility footprints
              and field confirmation — so underwriting, claims, and eligibility
              decisions rest on evidence instead of self-declarations.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <PillButton to="/request-access" variant="dark" size="lg" data-testid="hero-request-access-button">
                REQUEST ACCESS
              </PillButton>
              <PillButton to="/signin" variant="white" size="lg" arrow={false} data-testid="hero-signin-button">
                SIGN IN
              </PillButton>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-mono-label text-[9px] text-oath-muted">
              <span>● SIGNED CONSENT REQUIRED</span>
              <span>● IMMUTABLE AUDIT LOG</span>
              <span>● TRI-OUTCOME VERDICT</span>
            </div>
            <AvatarStack />
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-7xl px-6 lg:px-10 animate-fade-up" style={{ animationDelay: "200ms" }}>
          <HeroSiteMap />
        </div>
      </section>
      <CaseTicker />
      <Audiences />

      {/* ---------------- 01 PROBLEM ---------------- */}
      <section id="problem" className="scroll-mt-28 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <SectionLabel number="01">THE PROBLEM</SectionLabel>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-oath-ink sm:text-4xl">
              Declared addresses are <span className="text-oath-accent">cheap to claim</span>, costly to get wrong.
            </h2>
          </div>
          <ProblemStats />
        </div>
      </section>

      <Divider />

      {/* ---------------- 02 SERVICES ---------------- */}
      <section id="services" className="scroll-mt-28 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <SectionLabel number="02">CORE SERVICES</SectionLabel>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-oath-ink sm:text-4xl">
              Evidence-driven verification for the decisions that{" "}
              <span className="text-oath-accent">cost the most to get wrong</span>.
            </h2>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2" data-testid="instrument-grid">
            {INSTRUMENTS.map((s) => (
              <div key={s.title} data-testid={`instrument-card-${s.no.replace(/[^0-9]/g, "")}`}
                className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-lg hover:shadow-black/5">
                <div className="flex items-center justify-between">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-accent-light text-oath-accent"><s.icon className="h-5 w-5" strokeWidth={1.75} /></span>
                  <span className="font-mono-label text-[9px] text-oath-muted">{s.no}</span>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-oath-ink">{s.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-oath-secondary">{s.desc}</p>
                <ul className="mt-4 grid gap-1 text-xs text-oath-secondary">{s.tags.map((t) => <li key={t}>● {t}</li>)}</ul>
                <div className="mt-5 flex gap-6 border-t border-gray-100 pt-4 font-mono-label text-[9px] text-oath-muted">
                  {s.metrics.map(([k, v]) => <span key={k}>{k} · <b className="text-oath-ink">{v}</b></span>)}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 font-mono-label text-[10px] font-semibold text-oath-muted">WHERE IT'S USED</div>
          <div className="mt-4 rounded-3xl border border-gray-300/60 bg-oath-panel/50 p-3">
            <div className="grid gap-3 md:grid-cols-3">
              {SERVICES.map((s) => (
                <div
                  key={s.title}
                  data-testid={`service-card-${s.no.replace(/[^0-9]/g, "")}`}
                  className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-lg hover:shadow-black/5"
                >
                  <div className="flex items-center justify-between">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-accent-light text-oath-accent">
                      <s.icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <span className="font-mono-label text-[9px] text-oath-muted">{s.no}</span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold text-oath-ink">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-oath-secondary">{s.desc}</p>
                  <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                    {s.tags.map((t) => (
                      <span key={t} className="font-mono-label text-[8px] text-oath-muted">
                        ● {t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* ---------------- 03 PROCESS ---------------- */}
      <section id="process" className="scroll-mt-28 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <SectionLabel number="03">THE PROCESS</SectionLabel>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-oath-ink sm:text-4xl">
              From self-declaration to a{" "}
              <span className="text-oath-accent">signed, audit-ready report</span>.
            </h2>
          </div>

          <div className="mt-12 grid gap-4 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <div key={step.tag} className="relative">
                <div
                  data-testid={`process-step-${i + 1}`}
                  className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-shadow hover:shadow-lg hover:shadow-black/5"
                >
                  <img src={step.img} alt="" className="aspect-[4/3] w-full object-cover" data-testid={`process-step-${i + 1}-image`} />
                  <div className="flex flex-1 flex-col p-6">
                  <span className="font-mono-label text-[9px] font-semibold text-oath-accent">{step.tag}</span>
                  <span className="mt-4 grid h-11 w-11 place-items-center rounded-xl border border-gray-200 text-oath-ink">
                    <step.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-oath-ink">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-oath-secondary">{step.desc}</p>
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 lg:block">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M2 6 C 10 6, 14 18, 22 18" className="stroke-oath-accent" strokeWidth="1.5" fill="none" />
                      <path d="M18 14 L22 18 L18 22" className="stroke-oath-accent" strokeWidth="1.5" fill="none" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ---------------- 04 COVERAGE ---------------- */}
      <section id="coverage" className="scroll-mt-28 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <SectionLabel number="04">COVERAGE &amp; SCALE</SectionLabel>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-oath-ink sm:text-4xl">
              A national field network under{" "}
              <span className="text-oath-accent">one evidence standard</span>.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 rounded-3xl border border-gray-300/60 bg-oath-panel/50 p-3 lg:grid-cols-[1fr_1fr]">
            <CoverageStats />
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <USMap />
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* ---------------- 05 COMPLIANCE ---------------- */}
      <section id="compliance" className="scroll-mt-28 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <SectionLabel number="05">COMPLIANCE</SectionLabel>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-oath-ink sm:text-4xl">
              Built to withstand the{" "}
              <span className="text-oath-accent">audit you are dreading</span>.
            </h2>
          </div>

          <CertificationGrid />
        </div>
      </section>

      {/* ---------------- CLOSING CTA ---------------- */}
      <section className="pb-24 pt-4">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-10 lg:p-16">
            <div className="grid items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight text-oath-ink sm:text-4xl lg:text-5xl">
                  Ready to verify your first address?
                </h2>
                <p className="mt-4 max-w-md text-oath-secondary">
                  Console access is provisioned for approved organizations.
                  Request access and a Super Admin will review your permitted purposes.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <PillButton to="/signin" variant="dark" size="lg" data-testid="cta-signin-button">
                    SIGN IN TO THE CONSOLE
                  </PillButton>
                  <PillButton to="/request-access" variant="white" size="lg" arrow={false}>
                    REQUEST ACCESS
                  </PillButton>
                </div>
              </div>
              <div className="relative flex justify-center lg:justify-end">
                <img src="/img/people/family.jpg" alt="" className="aspect-[4/3] w-full max-w-md rounded-3xl object-cover" data-testid="cta-family-image" />
                <div className="absolute -bottom-6 -left-2 rounded-full bg-white p-2 shadow-lg sm:left-4"><VerifiedStamp size={110} /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
